import { AnimatedSection } from "@/components/common/AnimatedSection"
import { SectionLabel } from "@/components/common/SectionLabel"
import { Button } from "@/components/ui/Button"
import { useSEO } from "@/hooks/useSEO"
import { CTASection } from "@/sections/CTASection"
import { createFileRoute, Link } from "@tanstack/react-router"
import { AnimatePresence, motion } from "framer-motion"
import { ArrowRight, BarChart3, Bell, CheckCircle2, RefreshCw, TrendingUp } from "lucide-react"
import { useState } from "react"

export const Route = createFileRoute("/features/subscriptions")({
  component: SubscriptionsPage,
})

// ─── DATA ───────────────────────────────────────────────────────────────────

const BILLING_MODELS = [
  {
    id: "flat",
    label: "Flat-rate",
    icon: "",
    desc: "One price, one plan. Simple, predictable, easy to sell.",
    example: {
      lines: ["$99/month", "Unlimited users", "All features included"],
      badge: "Starter plan",
    },
    code: `orchestrate.subscriptions.create({\n  customer: 'cus_abc',\n  price: 'price_monthly_99',\n  trial_days: 14\n})`,
  },
  {
    id: "seat",
    label: "Per-seat",
    icon: "",
    desc: "Scales with your customer's team. Revenue grows with usage.",
    example: {
      lines: ["$29/seat/month", "12 seats active", "$348/month total"],
      badge: "Growth plan",
    },
    code: `orchestrate.subscriptions.create({\n  customer: 'cus_abc',\n  price: 'price_per_seat',\n  quantity: 12\n})`,
  },
  {
    id: "usage",
    label: "Usage-based",
    icon: "",
    desc: "Charge exactly for what's consumed. Ideal for APIs and infra.",
    example: {
      lines: ["$0.004 per API call", "2.1M calls this month", "$8,400 billed"],
      badge: "Metered billing",
    },
    code: `orchestrate.usageRecords.create({\n  subscription_item: 'si_abc',\n  quantity: 2100000,\n  action: 'set'\n})`,
  },
  {
    id: "hybrid",
    label: "Hybrid",
    icon: "",
    desc: "Base commitment plus variable overage. Best of both worlds.",
    example: {
      lines: ["$499/month base", "+$0.002 per extra call", "$731 total this month"],
      badge: "Enterprise plan",
    },
    code: `orchestrate.subscriptions.create({\n  customer: 'cus_abc',\n  items: [\n    { price: 'price_base' },\n    { price: 'price_overage' }\n  ]\n})`,
  },
]

// MRR data points (12 months)
const MRR_DATA = [41, 48, 52, 61, 67, 74, 82, 88, 96, 107, 118, 132]
const MRR_MAX = Math.max(...MRR_DATA)

const DUNNING_STEPS = [
  {
    day: "Day 0",
    title: "Payment fails",
    detail: "Card declined. Customer notified. No access lost yet.",
    color: "border-rose-DEFAULT/40 bg-rose-light",
    dot: "bg-rose-DEFAULT",
    text: "text-rose-DEFAULT",
  },
  {
    day: "Day 1",
    title: "Retry #1",
    detail: "Smart retry on a different BIN path. No email sent yet.",
    color: "border-amber-DEFAULT/40 bg-amber-light",
    dot: "bg-amber-DEFAULT",
    text: "text-amber-DEFAULT",
  },
  {
    day: "Day 3",
    title: "Retry #2 + Email",
    detail: '"Update your payment method" email sent. Grace period active.',
    color: "border-amber-DEFAULT/40 bg-amber-light",
    dot: "bg-amber-DEFAULT",
    text: "text-amber-DEFAULT",
  },
  {
    day: "Day 7",
    title: "Final retry",
    detail: "Last automated attempt. Urgency email dispatched.",
    color: "border-amber-DEFAULT/30 bg-amber-light/60",
    dot: "bg-amber-DEFAULT/60",
    text: "text-amber-DEFAULT",
  },
  {
    day: "Day 9",
    title: "Recovered ✓",
    detail: "Average 32% of at-risk MRR saved. Account restored instantly.",
    color: "border-green-DEFAULT/40 bg-green-light",
    dot: "bg-green-DEFAULT",
    text: "text-green",
  },
]

const CAPABILITIES = [
  {
    icon: RefreshCw,
    title: "Every billing model",
    items: [
      "Flat-rate & per-seat",
      "Usage-based metering",
      "Tiered & volume pricing",
      "Add-ons & one-time charges",
      "Multi-currency plans",
    ],
  },
  {
    icon: Bell,
    title: "Smart dunning engine",
    items: [
      "Configurable retry cadence",
      "Automated email sequences",
      "Network tokenisation",
      "Pre-dunning alerts",
      "Grace period controls",
    ],
  },
  {
    icon: TrendingUp,
    title: "Revenue optimisation",
    items: [
      "Automatic card updater",
      "Network token refresh",
      "Prorated upgrade/downgrade",
      "Trial conversion flows",
      "Churn prediction signals",
    ],
  },
  {
    icon: BarChart3,
    title: "SaaS analytics",
    items: [
      "Real-time MRR & ARR",
      "Churn & expansion revenue",
      "LTV by cohort & plan",
      "Trial → paid conversion",
      "BI export (CSV / API)",
    ],
  },
]

function SubscriptionsPage() {
  const [activeModel, setActiveModel] = useState(0)
  useSEO({
    title: "Subscriptions & Billing",
    description:
      "Flexible recurring billing, smart dunning, and SaaS analytics. Build any pricing model through a single API.",
    canonical: "https://orchestrate.global/features/subscriptions",
  })

  return (
    <>
      {/* ── HERO */}
      <section className="relative flex min-h-[90vh] flex-col justify-center overflow-hidden pt-28">
        <div className="grid-bg absolute inset-0 opacity-[0.12]" aria-hidden />
        {/* Gradient accent */}
        <div
          className="absolute top-[-200px] right-[-100px] h-[700px] w-[700px] rounded-full opacity-20"
          style={{
            background: "radial-gradient(circle, #1847F0 0%, transparent 60%)",
          }}
          aria-hidden
        />

        <div className="relative mx-auto max-w-7xl px-6 pt-16 pb-0 lg:px-8">
          <div className="grid items-center gap-16 pb-20 lg:grid-cols-2">
            {/* Left: Copy */}
            <div>
              <AnimatedSection delay={0.1}>
                <h1 className="font-display mb-8 text-6xl leading-[0.9] tracking-tight sm:text-7xl lg:text-[82px]">
                  Recurring
                  <br />
                  revenue,
                  <br />
                  <em className="from-blue-mid to-blue bg-gradient-to-r bg-clip-text text-transparent not-italic">
                    engineered.
                  </em>
                </h1>
              </AnimatedSection>
              <AnimatedSection delay={0.2}>
                <p className="mb-10 max-w-lg text-xl leading-relaxed">
                  Flat-rate, per-seat, usage-based, or hybrid — build any pricing model. Smart
                  dunning recovers what would otherwise churn. One API handles the whole lifecycle.
                </p>
              </AnimatedSection>
              <AnimatedSection delay={0.3}>
                <div className="flex flex-wrap gap-3">
                  <Button variant="primary" size="lg" asChild>
                    <Link to="/contact">
                      Start building <ArrowRight className="h-4 w-4" />
                    </Link>
                  </Button>
                  <Button variant="secondary" size="lg" asChild>
                    <a href="https://docs.orchestrate.global/">API reference</a>
                  </Button>
                </div>
              </AnimatedSection>

              {/* Inline metrics */}
              <AnimatedSection delay={0.4}>
                <div className="mt-12 grid grid-cols-3 gap-6 border-t border-gray-200 pt-10">
                  {[
                    { val: "32%", label: "Avg MRR recovered via dunning" },
                    { val: "1.8%", label: "Customer churn rate (our avg)" },
                    { val: "<48h", label: "Time to first subscription" },
                  ].map((s) => (
                    <div key={s.label}>
                      <p className="font-display text-blue text-3xl">{s.val}</p>
                      <p className="mt-1 text-xs leading-snug text-white/40">{s.label}</p>
                    </div>
                  ))}
                </div>
              </AnimatedSection>
            </div>

            {/* Right: Live MRR Chart */}
            <AnimatedSection direction="right" delay={0.2}>
              <div className="rounded-3xl border border-gray-200 bg-white p-7 backdrop-blur-sm">
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-bold tracking-widest uppercase">
                      Monthly Recurring Revenue
                    </p>
                    <p className="font-display mt-1 text-4xl">$132,440</p>
                    <span className="text-green mt-1 inline-flex items-center gap-1 text-xs font-bold">
                      <TrendingUp className="h-3 w-3" />
                      +12.1% vs last month
                    </span>
                  </div>
                  <span className="bg-green/20 text-green border-green/30 rounded-full border px-3 py-1.5 text-xs font-bold">
                    Live
                  </span>
                </div>

                {/* Bar chart */}
                <div className="mb-3 flex h-36 items-end gap-1.5">
                  {MRR_DATA.map((val, i) => (
                    <motion.div
                      key={i}
                      className={`flex-1 rounded-t-lg ${i === MRR_DATA.length - 1 ? "bg-blue" : "bg-blue/30"}`}
                      style={{ height: `${(val / MRR_MAX) * 100}%` }}
                      initial={{ scaleY: 0 }}
                      whileInView={{ scaleY: 1 }}
                      transition={{
                        duration: 0.6,
                        delay: 0.3 + i * 0.05,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                      viewport={{ once: true }}
                      whileHover={{ backgroundColor: "#1847F0" }}
                    />
                  ))}
                </div>
                <div className="mb-6 flex justify-between font-mono text-[10px]">
                  <span>Jan</span>
                  <span>Apr</span>
                  <span>Jul</span>
                  <span>Oct</span>
                  <span>Jan</span>
                </div>

                {/* MRR breakdown */}
                <div className="space-y-2 border-t border-gray-200 pt-5">
                  {[
                    {
                      label: "New MRR",
                      val: "+$18,340",
                      color: "text-green",
                    },
                    {
                      label: "Expansion MRR",
                      val: "+$4,210",
                      color: "text-blue-mid",
                    },
                    {
                      label: "Contraction MRR",
                      val: "-$1,840",
                      color: "text-amber-DEFAULT",
                    },
                    {
                      label: "Churned MRR",
                      val: "-$6,270",
                      color: "text-rose-DEFAULT",
                    },
                  ].map((row) => (
                    <div key={row.label} className="flex items-center justify-between">
                      <span className="text-xs">{row.label}</span>
                      <span className={`font-mono text-xs font-bold ${row.color}`}>{row.val}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>

        {/* Bottom: billing model tabs flush to section bottom */}
        <div className="mt-auto border-t border-gray-200">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <div className="grid grid-cols-2 sm:grid-cols-4">
              {BILLING_MODELS.map((m, i) => (
                <button
                  key={m.id}
                  onClick={() => setActiveModel(i)}
                  className={`border-r border-gray-200 px-6 py-5 text-left transition-all last:border-0 ${activeModel === i ? "bg-white" : "hover:bg-white"}`}
                >
                  <span className="mb-2 block text-xl">{m.icon}</span>
                  <p
                    className={`text-sm font-bold transition-colors ${activeModel === i ? "text-ink" : "text-gray-600"}`}
                  >
                    {m.label}
                  </p>
                  {activeModel === i && (
                    <motion.div
                      layoutId="sub-tab"
                      className="bg-blue mt-2 h-0.5 w-6 rounded-full"
                    />
                  )}
                </button>
              ))}
            </div>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeModel}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2 }}
                className="grid items-start gap-8 border-t border-white/10 p-8 lg:grid-cols-3"
              >
                <div className="lg:col-span-2">
                  <h3 className="font-display mb-2 text-2xl">
                    {BILLING_MODELS[activeModel].label} billing
                  </h3>
                  <p className="mb-6 text-sm">{BILLING_MODELS[activeModel].desc}</p>
                  <pre className="bg-ink text-blue-mid overflow-x-auto rounded-xl border border-white/10 p-5 font-mono text-xs leading-6">
                    <code>{BILLING_MODELS[activeModel].code}</code>
                  </pre>
                </div>
                <div className="rounded-2xl border border-gray-200 bg-white p-5">
                  <p className="mb-4 text-[10px] font-bold tracking-widest uppercase">
                    {BILLING_MODELS[activeModel].example.badge}
                  </p>
                  {BILLING_MODELS[activeModel].example.lines.map((line) => (
                    <div
                      key={line}
                      className="flex items-center gap-2 border-b border-white/10 py-2 last:border-0"
                    >
                      <CheckCircle2 className="text-green h-3.5 w-3.5 flex-shrink-0" />
                      <span className="text-sm">{line}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* ── DUNNING: light section, timeline visual ── */}
      <section className="bg-white py-28">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="grid items-center gap-20 lg:grid-cols-2">
            <AnimatedSection>
              <SectionLabel label="Dunning Engine" className="mb-6" />
              <h2 className="font-display mb-6 text-5xl tracking-tight sm:text-6xl">
                Recover what
                <br />
                should never
                <br />
                <em className="text-blue not-italic">have churned.</em>
              </h2>
              <p className="mb-8 text-lg leading-relaxed">
                Payment failures cause 20–40% of SaaS churn — and most of it is preventable. Our
                dunning engine retries intelligently, emails the right customer at the right moment,
                and recovers an average of 32% of at-risk MRR automatically.
              </p>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { val: "32%", label: "Avg MRR recovered" },
                  { val: "9 days", label: "Recovery window" },
                  { val: "3×", label: "Retry attempts" },
                  { val: "0", label: "Manual steps required" },
                ].map((s) => (
                  <div key={s.label} className="bg-surface border-border rounded-2xl border p-4">
                    <p className="font-display text-blue text-3xl">{s.val}</p>
                    <p className="mt-1 text-xs">{s.label}</p>
                  </div>
                ))}
              </div>
            </AnimatedSection>

            <AnimatedSection direction="right" delay={0.15}>
              <div className="space-y-3">
                {DUNNING_STEPS.map((step, i) => (
                  <motion.div
                    key={step.day}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{
                      delay: i * 0.1,
                      duration: 0.5,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                    viewport={{ once: true }}
                  >
                    <div className={`flex items-start gap-4 rounded-2xl p-5 ${step.color}`}>
                      <div className="flex flex-shrink-0 flex-col items-center gap-1">
                        <div className={`h-3 w-3 rounded-full ${step.dot}`} />
                        {i < DUNNING_STEPS.length - 1 && <div className="bg-border h-4 w-px" />}
                      </div>
                      <div className="flex-1">
                        <div className="mb-1 flex items-center gap-3">
                          <span
                            className={`text-xs font-bold tracking-widest uppercase ${step.text}`}
                          >
                            {step.day}
                          </span>
                          <span className="text-sm font-semibold">{step.title}</span>
                        </div>
                        <p className="text-muted text-xs leading-relaxed">{step.detail}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* ── CAPABILITIES GRID ── */}
      <section className="bg-surface py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <AnimatedSection>
            <div className="mb-16 text-center">
              <SectionLabel label="Everything included" centered className="mb-5" />
              <h2 className="font-display text-5xl tracking-tight">
                The complete billing
                <em className="text-blue not-italic"> stack.</em>
              </h2>
            </div>
          </AnimatedSection>
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {CAPABILITIES.map((cap, i) => {
              const Icon = cap.icon
              return (
                <motion.div
                  key={cap.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: i * 0.1,
                    duration: 0.6,
                    ease: [0.16, 1, 0.3, 1],
                  }}
                  viewport={{ once: true }}
                >
                  <div className="group border-border hover:border-blue/30 h-full rounded-3xl border bg-white p-7 transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl">
                    <div
                      className={`bg-blue-light mb-6 flex h-12 w-12 items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110`}
                    >
                      <Icon className={`text-blue h-5 w-5`} />
                    </div>
                    <h3 className="mb-4 text-base font-bold">{cap.title}</h3>
                    <ul className="space-y-2">
                      {cap.items.map((item) => (
                        <li key={item} className="text-muted flex items-center gap-2 text-xs">
                          <div className="bg-blue h-1 w-1 flex-shrink-0 rounded-full" />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* ── LIFECYCLE FLOW ── */}
      <section className="bg-ink relative overflow-hidden py-24">
        <div className="dot-bg absolute inset-0 opacity-[0.07]" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
          <AnimatedSection>
            <div className="mb-16 text-center">
              <SectionLabel label="Subscription lifecycle" centered className="mb-5" />
              <h2 className="font-display text-4xl tracking-tight text-white sm:text-5xl">
                From trial to
                <em className="text-blue-mid not-italic"> loyal customer.</em>
              </h2>
            </div>
          </AnimatedSection>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-6">
            {[
              {
                icon: "",
                title: "Trial starts",
                desc: "Free trial created via API. Trial days configurable per plan.",
              },
              {
                icon: "",
                title: "Card captured",
                desc: "Card saved during trial. No charge yet — trust preserved.",
              },
              {
                icon: "",
                title: "Trial ending",
                desc: "Automated email 3 days before. Conversion prompt delivered.",
              },
              {
                icon: "",
                title: "Subscription active",
                desc: "First charge succeeds. Customer enters billing lifecycle.",
              },
              {
                icon: "",
                title: "Upgraded",
                desc: "Plan change prorated automatically. No billing complexity.",
              },
              {
                icon: "",
                title: "Renewed",
                desc: "Auto-renews with card updater + network token. Silent, seamless.",
              },
            ].map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{
                  delay: i * 0.08,
                  duration: 0.5,
                  ease: [0.16, 1, 0.3, 1],
                }}
                viewport={{ once: true }}
              >
                <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-5 text-center transition-colors hover:bg-white/8">
                  <div className="mb-3 text-3xl">{s.icon}</div>
                  <p className="mb-2 text-sm font-bold text-white">{s.title}</p>
                  <p className="text-xs leading-relaxed text-white/35">{s.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <CTASection />
    </>
  )
}
